import React, { useCallback } from 'react';
import { Icon } from '../../../pages/DesignSystem';
import EventCardMenu from '../EventList/EventCardMenu';
import { Event } from '../../../types/event.types';
import type { EventMetrics } from '../../../types/organizer-events.types';
import './PlanningEventCard.css';

interface PlanningEventCardProps {
  event: Event;
  metrics: EventMetrics | null;
  metricsLoading: boolean;
  onEdit: (event: Event) => void;
  onDuplicate: (event: Event) => void;
  onCancel?: (event: Event) => void;
  onRestore?: (event: Event) => void;
  onPublish?: (event: Event) => void;
  onDelete?: (event: Event) => void;
  onInstagram?: (event: Event) => void;
  onAddOccurrence?: (event: Event) => void;
}

/**
 * Format event time in the event's timezone: "09:00"
 */
const formatTime = (datetime: Date, tz?: string): string => {
  const d = new Date(datetime);
  return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: tz || undefined });
};

/**
 * Truncate title to ~25 chars
 */
const truncateTitle = (title: string, maxLen = 25): string => {
  if (title.length <= maxLen) return title;
  return title.slice(0, maxLen).trimEnd() + '...';
};

/**
 * Get impressions label
 */
const getViewsLabel = (impressions: number): string => {
  if (impressions === 0) return 'No views yet';
  return `Seen by ${impressions} ${impressions === 1 ? 'tourist' : 'tourists'}`;
};

/**
 * Check if event spans into next day
 */
const isMultiDay = (event: Event): boolean => {
  if (!event.datetime || !event.endDateTime) return false;
  const start = new Date(event.datetime);
  const end = new Date(event.endDateTime);
  return start.toDateString() !== end.toDateString();
};

const PlanningEventCard: React.FC<PlanningEventCardProps> = ({
  event,
  metrics,
  metricsLoading,
  onEdit,
  onDuplicate,
  onCancel,
  onRestore,
  onPublish,
  onDelete,
  onInstagram,
  onAddOccurrence,
}) => {
  const isRecurring = !!event.seriesId;
  const isCancelled = event.status === 'cancelled';
  const isDraft = event.status === 'draft';
  const isPast = event.datetime ? new Date(event.datetime) < new Date() : false;

  const impressions = metrics?.impressions ?? 0;

  const statusClass = isCancelled
    ? 'planning-card--cancelled'
    : isDraft
    ? 'planning-card--draft'
    : isPast
    ? 'planning-card--past'
    : '';

  const handleCardClick = useCallback(() => {
    if (!isPast) {
      onEdit(event);
    }
  }, [event, isPast, onEdit]);

  const handleDuplicate = useCallback(() => {
    // Create a duplicate without id (treated as new creation)
    const { id, ...rest } = event;
    onDuplicate({ ...rest, status: 'draft' } as Event);
  }, [event, onDuplicate]);

  // Determine activeTab from event status for the menu
  const activeTab = isCancelled ? 'active' : isDraft ? 'draft' : isPast ? 'past' : 'active';

  return (
    <div
      className={`planning-card ${statusClass}`}
      onClick={handleCardClick}
      role="button"
      tabIndex={0}
    >
      {/* Left color bar */}
      <div
        className={`planning-card__bar ${isRecurring ? 'planning-card__bar--recurring' : 'planning-card__bar--single'}`}
      />

      <div className="planning-card__content">
        {/* Line 1: time block + title + icons */}
        <div className="planning-card__line1">
          <div className="planning-card__time-block">
            <span className="planning-card__time">
              {event.datetime ? formatTime(event.datetime, event.timezone) : '--:--'}
            </span>
            {event.endTime && (
              <span className="planning-card__time-end">
                {event.endTime.includes('T')
                  ? formatTime(new Date(event.endTime), event.timezone)
                  : event.endTime}
              </span>
            )}
          </div>
          <span className="planning-card__title">
            {truncateTitle(event.title || 'Untitled')}
          </span>
          {isMultiDay(event) && (
            <span className="planning-card__multiday">→ +1</span>
          )}
          {isDraft && (
            <span className="planning-card__chip planning-card__chip--draft">Draft</span>
          )}
          {isCancelled && (
            <span className="planning-card__chip planning-card__chip--cancelled">CANCELLED</span>
          )}
          {isRecurring && (
            <Icon name="Repeat" size={14} className="planning-card__repeat-icon" />
          )}

          <div className="planning-card__menu" onClick={(e) => e.stopPropagation()}>
            <EventCardMenu
              eventId={event.id}
              eventStatus={event.status}
              seriesId={event.seriesId}
              activeTab={activeTab}
              isSeriesMenu={isRecurring}
              onEdit={() => onEdit(event)}
              onDuplicate={handleDuplicate}
              onAddOccurrence={isRecurring && onAddOccurrence ? () => onAddOccurrence(event) : undefined}
              onCancel={onCancel ? () => onCancel(event) : undefined}
              onRestore={onRestore ? () => onRestore(event) : undefined}
              onPublish={onPublish ? () => onPublish(event) : undefined}
              onDelete={onDelete ? () => onDelete(event) : undefined}
              onInstagram={onInstagram ? () => onInstagram(event) : undefined}
            />
          </div>
        </div>

        {/* Line 2: location */}
        {(event.locationName || event.location) && (
          <div className="planning-card__line2">
            <Icon name="MapPin" size={12} className="planning-card__location-icon" />
            <span className="planning-card__location">
              {event.locationName || event.location}
            </span>
          </div>
        )}

        {/* Recurrence pattern */}
        {isRecurring && event.weekDays && event.weekDays.length > 0 && (
          <div className="planning-card__recurrence">
            <Icon name="Repeat" size={11} />
            <span>Every {event.weekDays.map(d => d.charAt(0).toUpperCase() + d.slice(1, 3)).join(', ')}</span>
          </div>
        )}

        {/* Line 3: impressions + publish button */}
        <div className="planning-card__line3">
          <div className="planning-card__impressions">
            <Icon name="Eye" size={12} />
            <span>{metricsLoading && !metrics ? '...' : impressions}</span>
          </div>
          {isDraft && onPublish && (
            <button
              className="planning-card__publish-btn"
              onClick={(e) => {
                e.stopPropagation();
                onPublish(event);
              }}
              type="button"
            >
              <Icon name="Send" size={11} />
              <span>Publish</span>
            </button>
          )}
          {!isDraft && event.status === 'active' && onInstagram && (
            <button
              className="planning-card__instagram-btn"
              onClick={(e) => {
                e.stopPropagation();
                onInstagram(event);
              }}
              type="button"
              title="Generate Instagram post"
            >
              <Icon name="Instagram" size={14} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default React.memo(PlanningEventCard);
